import { SITE, HOTEL, SOCIAL, AMENITIES, ROOM_KEYS, ROOM_BEDS, LANG_TAGS, absUrl } from './hotelFacts';
import { seoFor, DISTANCES } from '../i18n/seoLanding';
import { ROOM_SLUGS } from '../i18n/roomPages';

export const roomUrl = (room, lang) => (ROOM_SLUGS[room.beds24Id] ? absUrl(lang, `rooms/${ROOM_SLUGS[room.beds24Id]}`) : absUrl(lang, 'rooms'));

export const nid = (fragment) => `${SITE}/#${fragment}`;
const feature = (name) => ({ '@type': 'LocationFeatureSpecification', name, value: true });
const sqm = (size) => parseFloat(String(size || '').replace(',', '.')) || undefined;

const roomCopy = (room, lang, t) => {
  const key = ROOM_KEYS[room.beds24Id];
  if (lang === 'en' || !key) return [room.name, room.description];
  return [t(`roomTypes.${key}`), t(`roomTypes.${key}Desc`)];
};

export const roomNode = (room, lang, t) => {
  const [name, description] = roomCopy(room, lang, t);
  const bed = ROOM_BEDS[room.beds24Id];
  return {
    '@type': /apartment/i.test(room.name) ? 'Suite' : 'HotelRoom',
    '@id': nid(`room-${room.beds24Id}`),
    url: roomUrl(room, lang),
    name,
    description,
    image: room.images || [],
    occupancy: { '@type': 'QuantitativeValue', minValue: 1, maxValue: room.guests },
    floorSize: { '@type': 'QuantitativeValue', value: sqm(room.size), unitCode: 'MTK' },
    ...(bed ? { bed: { '@type': 'BedDetails', typeOfBed: bed, numberOfBeds: 1 } } : {}),
    amenityFeature: (room.amenities || []).map(feature),
  };
};

export const offerNode = (room, lang, t, url) => ({
  '@type': 'Offer',
  name: `${roomCopy(room, lang, t)[0]} — ${t('rooms.startingFrom')} €${Math.round(room.price)}${t('rooms.perNight')}`,
  price: String(Math.round(room.price)),
  priceCurrency: 'EUR',
  priceSpecification: { '@type': 'UnitPriceSpecification', price: String(Math.round(room.price)), priceCurrency: 'EUR', unitText: 'night' },
  availability: 'https://schema.org/InStock',
  url: url || absUrl(lang, 'rooms'),
  itemOffered: { '@id': nid(`room-${room.beds24Id}`) },
});

const hotelNode = (lang, t, rooms) => {
  const prices = rooms.map((r) => Math.round(r.price)).filter(Boolean);
  return {
    '@type': 'Hotel',
    '@id': nid('hotel'),
    name: HOTEL.name,
    alternateName: HOTEL.alternateNames,
    logo: { '@type': 'ImageObject', url: `${SITE}/logo.png`, width: 512, height: 512 },
    description: seoFor(lang, 'home').page.intro,
    url: absUrl(lang, ''),
    image: rooms.map((r) => r.images?.[0]).filter(Boolean),
    telephone: HOTEL.phone,
    email: HOTEL.email,
    address: { '@type': 'PostalAddress', streetAddress: HOTEL.street, addressLocality: HOTEL.city, postalCode: HOTEL.postalCode, addressCountry: HOTEL.countryCode },
    geo: { '@type': 'GeoCoordinates', latitude: HOTEL.lat, longitude: HOTEL.lng },
    hasMap: HOTEL.mapUrl,
    starRating: { '@type': 'Rating', ratingValue: '4', bestRating: '5' },
    smokingAllowed: false,
    checkinTime: HOTEL.checkin,
    checkoutTime: HOTEL.checkout,
    priceRange: prices.length ? `€${Math.min(...prices)} – €${Math.max(...prices)}` : '€€',
    currenciesAccepted: 'EUR',
    paymentAccepted: 'Cash, Credit Card, Debit Card, Bank Transfer',
    availableLanguage: HOTEL.spokenLanguages,
    amenityFeature: AMENITIES.map(feature),
    containsPlace: rooms.map((r) => roomNode(r, lang, t)),
    makesOffer: rooms.map((r) => offerNode(r, lang, t, roomUrl(r, lang))),
    sameAs: SOCIAL,
    potentialAction: {
      '@type': 'ReserveAction',
      target: { '@type': 'EntryPoint', urlTemplate: absUrl(lang, 'rooms'), actionPlatform: ['https://schema.org/DesktopWebPlatform', 'https://schema.org/MobileWebPlatform'] },
      result: { '@type': 'LodgingReservation', name: 'Hotel reservation' },
    },
  };
};

const PAGE_TYPES = { faq: 'FAQPage', contact: 'ContactPage', gallery: 'ImageGallery' };

const mainEntity = (slug, lang, t, rooms) => {
  if (!slug || slug === 'amenities') return { '@id': nid('hotel') };
  if (slug === 'rooms') {
    return { '@type': 'ItemList', itemListElement: rooms.map((r, i) => ({ '@type': 'ListItem', position: i + 1, item: { '@id': nid(`room-${r.beds24Id}`) } })) };
  }
  if (slug === 'faq') {
    const items = t('faq.items');
    return (Array.isArray(items) ? items : []).map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } }));
  }
  if (slug === 'location') {
    const places = t('explore.attractions') || {};
    return {
      '@type': 'ItemList',
      itemListElement: DISTANCES.map(([key, km], i) => ({
        '@type': 'ListItem', position: i + 1,
        item: { '@type': 'TouristAttraction', name: places[key]?.name || key, description: `${places[key]?.desc || ''} (${String(km).replace('.', ',')} km)`.trim() },
      })),
    };
  }
  return undefined;
};

// Full schema.org graph for one page in one language — replaces the static block in index.html at runtime (bots receive the rendered DOM).
// `extra` lets detail pages add their own page type, main entity, breadcrumb trail and nodes: { pageType, mainEntity, crumbs: [{name, item}], nodes: [], image }.
export const buildGraph = ({ language, slug = '', url, title, description, t, rooms = [], extra = {} }) => {
  const homeUrl = absUrl(language, '');
  const short = slug ? (t(`pages.${slug}`)?.title || slug) : '';
  const images = rooms.flatMap((r) => r.images || []).slice(0, 24);
  const entity = extra.mainEntity || mainEntity(slug, language, t, rooms);
  const primaryImage = extra.image || images[0];

  const webPage = {
    '@type': extra.pageType || PAGE_TYPES[slug] || 'WebPage',
    '@id': url,
    url,
    name: title,
    description,
    inLanguage: LANG_TAGS[language] || language,
    isPartOf: { '@id': nid('website') },
    about: { '@id': nid('hotel') },
    breadcrumb: { '@id': `${url}#breadcrumb` },
    ...(primaryImage ? { primaryImageOfPage: { '@type': 'ImageObject', url: primaryImage } } : {}),
    ...(slug === 'gallery' ? { image: images } : {}),
    ...(entity ? { mainEntity: entity } : {}),
  };

  const trail = extra.crumbs || (slug ? [{ name: short, item: url }] : []);
  const breadcrumb = {
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('pages.home'), item: homeUrl },
      ...trail.map((c, i) => ({ '@type': 'ListItem', position: i + 2, name: c.name, item: c.item })),
    ],
  };

  const website = {
    '@type': 'WebSite',
    '@id': nid('website'),
    url: `${SITE}/`,
    name: HOTEL.name,
    inLanguage: Object.values(LANG_TAGS),
    publisher: { '@id': nid('hotel') },
  };

  return [hotelNode(language, t, rooms), website, webPage, breadcrumb, ...(extra.nodes || [])];
};
